import type { FilterOption, FilterValue } from '@/types/filters.types';

/**
 * Patient-match rules for the Lab Results list, kept out of LabOrdersPage.tsx
 * so they can be tested.
 *
 * Page components under src/pages/ cannot be imported by a test — their module
 * graph reaches src/main.tsx, which calls createRoot() at import time — so any
 * logic left inline in the page is untestable by construction. Same reason
 * pages/tasks/taskPayload.ts exists.
 */

/** null = no filter (show everything). */
export type PatientMatch = 'unmatched' | 'matched' | null;

/** Must stay in step with LabResultsListQuery::normalizePatientMatch() (api). */
const PATIENT_MATCH_VALUES = ['unmatched', 'matched'] as const;

/** The query-string key the Labs nav badge links with. */
export const PATIENT_MATCH_PARAM = 'patientMatch';

export const PATIENT_MATCH_OPTIONS: FilterOption[] = [
  { value: 'unmatched', label: 'Not matched to a patient' },
  { value: 'matched', label: 'Matched to a patient' },
];

function isPatientMatch(value: string): value is Exclude<PatientMatch, null> {
  return (PATIENT_MATCH_VALUES as readonly string[]).includes(value);
}

/**
 * Read the filter out of a URL. Anything unrecognised means "no filter" rather
 * than an error, so a hand-edited or stale link still opens the list.
 */
export function readPatientMatchParam(search: URLSearchParams | string): PatientMatch {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get(PATIENT_MATCH_PARAM);

  return raw !== null && isPatientMatch(raw) ? raw : null;
}

/** FilterSidebar represents "no selection" as an empty array. */
export function patientMatchToFilterValue(value: PatientMatch): string[] {
  return value === null ? [] : [value];
}

export function patientMatchFromFilterValue(value: FilterValue): PatientMatch {
  if (!Array.isArray(value)) return null;
  const [first] = value;

  return typeof first === 'string' && isPatientMatch(first) ? first : null;
}

/** A lab result with no patient record attached. The API sends 0, never null. */
export function isUnmatchedResult(row: { patientId: number }): boolean {
  return row.patientId === 0;
}

/**
 * Row tint for the list. Legacy reddened the whole row (labs_full_list.php);
 * V5 cells set their own text colours, so a background is what actually shows.
 *
 * The hover variant is required, not decoration: DataTable applies
 * `hover:bg-slate-50` for clickable rows and merges this string afterwards, so
 * without it the tint disappears under the cursor.
 */
export function labResultRowClassName(row: { patientId: number }): string | undefined {
  return isUnmatchedResult(row) ? 'bg-red-50 hover:bg-red-100' : undefined;
}

/**
 * Who may delete lab results that belong to no patient. Must stay in step with
 * LabOrdersController::destroyUnmatched() (api), which enforces the same bar.
 */
export const MIN_LEVEL_TO_DELETE = 3;

export function canDeleteLabResults(userLevel: number | null | undefined): boolean {
  return (userLevel ?? 0) >= MIN_LEVEL_TO_DELETE;
}

/** Only unmatched results can be deleted; a matched one lives in a chart. */
export function canDeleteLabResult(
  row: { patientId: number },
  userLevel: number | null | undefined,
): boolean {
  return canDeleteLabResults(userLevel) && isUnmatchedResult(row);
}

/**
 * The results a bulk delete may actually send. A selection outlives the rows it
 * was made from (a result matched in another tab, a refetch under a standing
 * selection), so it is re-checked against the rows on screen every time: keys
 * no longer on the page, and rows that now have a patient, are dropped.
 */
export function selectedDeleteItems<T extends { rowKey: string; source: string; labResultId: number; patientId: number }>(
  selectedKeys: ReadonlySet<string | number>,
  rows: readonly T[],
): Array<{ source: T['source']; id: number }> {
  return rows
    .filter((row) => selectedKeys.has(row.rowKey) && isUnmatchedResult(row))
    .map((row) => ({ source: row.source, id: row.labResultId }));
}

/**
 * Where the Labs nav item points. With unmatched results waiting, it lands on
 * them directly — a count you cannot act on is just noise.
 */
export function labsNavHref(unmatchedCount?: number | null): string {
  return unmatchedCount != null && unmatchedCount > 0
    ? `/lab-orders?${PATIENT_MATCH_PARAM}=unmatched`
    : '/lab-orders';
}
